import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ApnsPushODataInterface, isApnsPushObject } from '../../types/apns-push.';
import { OnlineOnlyService } from '../online-only/online-only.service';
import { UserMasterService } from '../user-master/user-master.service';
import { AuthenticationInterface } from './authentication.interface';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class AuthenticationService implements AuthenticationInterface {
    private readonly connectionUrl: string;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly httpClient: HttpClient,
        private readonly userMasterService: UserMasterService,
    ) {
        this.connectionUrl = this.uriUtilities.getUriForClientAuthentication();
    }

    public performColdStartAuthentication(): Observable<boolean> {
        return this.httpClient.get(this.connectionUrl).pipe(
            map(() => true),
            catchError((err) => {
                this.log.error(
                    `AuthenticationService: Error performing cold start authentication: ${JSON.stringify(err)}`,
                );
                return of(false);
            }),
        );
    }

    public registerAPNSForPushNotifications(deviceToken: string): Observable<ApnsPushODataInterface | void> {
        // just to make sure we never execute the push code for windows
        if (this.clientManager.isWindows()) {
            return of(undefined);
        }
        const userModel = this.userMasterService.getSelectedUserModel();
        const body = {
            FrontId: userModel.getFrontId(),
            SalesOrg: userModel.getSalesOrg(),
            Plant: userModel.getPlant(),
            ApnsID: deviceToken,
            AppID: userModel.getAppId(),
        };

        return this.onlineOnlyService.post<ApnsPushODataInterface>(ENDPOINTS.RegisterApnsPushServiceSet, body).pipe(
            map((response: ApnsPushODataInterface) => {
                // we do not want to stop app start if push registration fails, continue to next step and just log error
                if (!isApnsPushObject(response)) {
                    this.log.error(`AuthenticationService: registerAPNSForPushNotifications: invalid apns response`);
                }
                return response;
            }),
            catchError((error: unknown) => {
                this.log.error(
                    `AuthenticationService: registerAPNSForPushNotifications: error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return throwError(
                    `AuthenticationService: registerAPNSForPushNotifications: error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
            }),
        );
    }
}
